import { startTransition } from 'react';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { isAmbiguousAnnualApyString } from '../../utils/calculatorFormMath';
import type { CdCalculatorStore } from './types';
import {
  type CalculateCdInterestOptions,
  calculateCdInterest,
  transformValuesToString,
} from './utils';
import { validate } from './validators';

export const defaultFormFieldValues: CdCalculatorStore['formFields'] = {
  initialDeposit: '10000',
  annualPercentageYield: '4.35',
  termLengthUnit: 'months',
  termLength: '12',
};

type UpdateFieldOptions = {
  field: keyof CdCalculatorStore['formFields'];
  value: string;
  formFields: CdCalculatorStore['formFields'];
  set: (fn: (state: CdCalculatorStore) => Partial<CdCalculatorStore>) => void;
  additionalState?: Partial<CdCalculatorStore>;
  finalize?: boolean;
  cdInterestOptions?: CalculateCdInterestOptions;
};

function validateAndUpdateFieldSync({
  field,
  value,
  formFields,
  set,
  additionalState = {},
  finalize = false,
  cdInterestOptions,
}: UpdateFieldOptions) {
  if (field === 'annualPercentageYield' && typeof value === 'string' && !finalize) {
    if (isAmbiguousAnnualApyString(value)) {
      set(state => ({
        formFields: {
          ...state.formFields,
          [field]: value,
        },
      }));
      return;
    }
  }

  const newFormFields = {
    ...formFields,
    [field]: value,
  };
  const result = validate(newFormFields);

  if (!result.success) {
    set(state => ({
      ...additionalState,
      formFields: {
        ...state.formFields,
        [field]: value,
      },
      errors: result.errors,
    }));
    return;
  }

  const nextResults = calculateCdInterest(result.output, cdInterestOptions);
  const validatedFormFields = transformValuesToString(result.output);
  set(() => ({
    ...additionalState,
    formFields: validatedFormFields,
    results: nextResults,
    lastValidResults: nextResults,
    errors: {},
  }));
}

function runDeferredValidationAndResults(
  get: () => CdCalculatorStore,
  set: (fn: (state: CdCalculatorStore) => Partial<CdCalculatorStore>) => void,
  cdInterestOptions?: CalculateCdInterestOptions,
) {
  const { formFields } = get();

  if (isAmbiguousAnnualApyString(formFields.annualPercentageYield)) {
    return;
  }

  const result = validate(formFields);

  if (!result.success) {
    set(() => ({
      errors: result.errors,
    }));
    return;
  }

  const nextResults = calculateCdInterest(result.output, cdInterestOptions);
  const validatedFormFields = transformValuesToString(result.output);
  set(() => ({
    formFields: {
      ...validatedFormFields,
      annualPercentageYield: formFields.annualPercentageYield.toString(),
    },
    results: nextResults,
    lastValidResults: nextResults,
    errors: {},
  }));
}

let useCdCalculatorStore: UseBoundStore<StoreApi<CdCalculatorStore>>;

export type CreateCdCalculatorStoreOptions = {
  nationalAvgApy?: number;
};

export function createCdCalculatorStore(
  defaultFormValues?: CdCalculatorStore['formFields'],
  storeOptions?: CreateCdCalculatorStoreOptions,
) {
  let defaultValues = defaultFormFieldValues;

  if (defaultFormValues) {
    defaultValues = {
      ...defaultValues,
      ...defaultFormValues,
    };
  }

  const cdInterestOptions: CalculateCdInterestOptions | undefined =
    storeOptions?.nationalAvgApy != null &&
    Number.isFinite(storeOptions.nationalAvgApy) &&
    storeOptions.nationalAvgApy > 0
      ? { nationalAvgApy: storeOptions.nationalAvgApy }
      : undefined;

  const defaultFormFieldsForValidation: CdCalculatorStore['formFields'] = {
    initialDeposit: defaultValues.initialDeposit,
    annualPercentageYield: defaultValues.annualPercentageYield,
    termLengthUnit: defaultValues.termLengthUnit,
    termLength: defaultValues.termLength,
  };

  const defaultValidation = validate(defaultFormFieldsForValidation);
  if (!defaultValidation.success) {
    throw new Error('One of the default CD calculator form fields is invalid');
  }

  const defaultResults = calculateCdInterest(defaultValidation.output, cdInterestOptions);
  const initialFormFields = transformValuesToString(defaultValidation.output);

  const store = create<CdCalculatorStore>((set, get) => {
    let deferredValidationTimerId: ReturnType<typeof setTimeout> | null = null;

    function scheduleDeferredValidation() {
      if (deferredValidationTimerId !== null) {
        clearTimeout(deferredValidationTimerId);
      }
      deferredValidationTimerId = setTimeout(() => {
        deferredValidationTimerId = null;
        requestAnimationFrame(() => {
          startTransition(() => {
            runDeferredValidationAndResults(get, set, cdInterestOptions);
          });
        });
      }, 100);
    }

    return {
      formFields: initialFormFields,
      results: defaultResults,
      lastValidResults: defaultResults,
      errors: {},
      setField: (field, value, options) => {
        if (options?.finalize) {
          const { formFields } = get();
          validateAndUpdateFieldSync({
            field,
            value,
            formFields,
            set,
            finalize: true,
            cdInterestOptions,
          });
          return;
        }

        set(state => ({
          formFields: {
            ...state.formFields,
            [field]: value,
          },
        }));
        scheduleDeferredValidation();
      },
    };
  });
  useCdCalculatorStore = store;
  return store;
}

export { useCdCalculatorStore };
